import React, { useState, useEffect } from "react";
import { Sparkles, Send, CheckCircle2, ClipboardCheck, Flag, AlertOctagon } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { TEAM_META, STATUS_META, SeverityChip, StatusChip, TeamChip, fmtRel, fmtDuration } from "./shared";

const ASSIGNEES = ["Team Alpha", "Team Bravo", "Team Charlie", "Team Delta", "Team Echo"];
const TEAMS = Object.keys(TEAM_META);

const Stage = ({ active, done, label }) => (
  <div className="flex items-center gap-1.5 min-w-0">
    <span
      className="w-2 h-2"
      style={{
        background: done ? "hsl(var(--success))" : active ? "hsl(var(--primary))" : "hsl(var(--border))",
      }}
    />
    <span
      className={[
        "font-mono text-[10px] uppercase tracking-widest truncate",
        done ? "text-[hsl(var(--success))]" : active ? "text-foreground" : "text-muted-foreground",
      ].join(" ")}
    >
      {label}
    </span>
  </div>
);

export const IncidentDetail = ({ incident, onUpdated }) => {
  const [notes, setNotes] = useState(incident?.notes || "");
  const [team, setTeam] = useState(incident?.team_type || "security");
  const [assignee, setAssignee] = useState(incident?.assignee || "");

  useEffect(() => {
    setNotes(incident?.notes || "");
    setTeam(incident?.team_type || "security");
    setAssignee(incident?.assignee || "");
  }, [incident?.id, incident?.notes, incident?.team_type, incident?.assignee]);

  if (!incident) {
    return (
      <div className="h-full flex items-center justify-center p-6 text-center text-xs text-muted-foreground">
        Select an incident from the list to inspect.
      </div>
    );
  }

  const patch = async (body, successMsg) => {
    try {
      const r = await http.patch(`/incidents/${incident.id}`, body);
      onUpdated?.(r.data);
      if (successMsg) toast.success(successMsg);
      return r.data;
    } catch {
      toast.error("Update failed");
    }
  };

  const stage = incident.status;
  const stageOrder = ["ack", "dispatched", "resolved", "reviewed"];
  const idx = stageOrder.indexOf(stage);

  const sla = incident.resolved_at
    ? (new Date(incident.resolved_at) - new Date(incident.created_at)) / 1000
    : (Date.now() - new Date(incident.created_at).getTime()) / 1000;

  return (
    <div className="h-full flex flex-col min-h-0 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2 flex-wrap">
          <SeverityChip sev={incident.severity} />
          <StatusChip status={incident.status} />
          <TeamChip team={incident.team_type} />
          {incident.false_positive && (
            <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-[hsl(var(--muted-foreground))] text-muted-foreground">
              False Positive
            </span>
          )}
          <span className="ml-auto font-mono text-[10px] text-muted-foreground">id {incident.id.slice(0, 8)}</span>
        </div>
        <h2 className="font-display font-black uppercase tracking-tight text-base leading-tight mt-2">
          {incident.title}
        </h2>
        <div className="mt-1 font-mono text-[11px] text-muted-foreground">
          {incident.camera_id ?? "—"} · {incident.zone_name} · created {fmtRel(incident.created_at)} · SLA {fmtDuration(sla)}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto divide-y divide-border min-h-0">
        {/* Workflow stages */}
        <section className="p-4">
          <div className="h-widget mb-2">Workflow</div>
          <div className="grid grid-cols-4 gap-2">
            <Stage label="Acknowledged" done={idx > 0} active={idx === 0} />
            <Stage label="Dispatched"   done={idx > 1} active={idx === 1} />
            <Stage label="Resolved"     done={idx > 2} active={idx === 2} />
            <Stage label="Reviewed"     done={idx > 3} active={idx === 3} />
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {incident.status === "ack" && (
              <button
                className="btn-tac-primary"
                onClick={() => patch({ status: "dispatched", team_type: team, assignee: assignee || null }, "Team dispatched")}
                data-testid="incident-dispatch-btn"
              >
                <Send className="w-3.5 h-3.5" /> Dispatch {team}
              </button>
            )}
            {incident.status === "dispatched" && (
              <button
                className="btn-tac-primary"
                onClick={() => patch({ status: "resolved" }, "Incident resolved")}
                data-testid="incident-resolve-btn"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Resolve
              </button>
            )}
            {incident.status === "resolved" && (
              <>
                <button
                  className="btn-tac"
                  onClick={() => patch({ status: "reviewed" }, "Marked reviewed")}
                  data-testid="incident-review-btn"
                >
                  <ClipboardCheck className="w-3.5 h-3.5" /> Mark Reviewed
                </button>
                <button
                  className="btn-tac"
                  onClick={() => patch({ false_positive: !incident.false_positive }, incident.false_positive ? "Cleared false-positive" : "Flagged false-positive")}
                  data-testid="incident-fp-btn"
                >
                  <Flag className="w-3.5 h-3.5" /> {incident.false_positive ? "Clear False+" : "Flag False+"}
                </button>
              </>
            )}
            {incident.status !== "resolved" && incident.status !== "reviewed" && (
              <button
                className="btn-tac-danger"
                onClick={() => patch({ status: "resolved", false_positive: false }, "Force closed")}
                data-testid="incident-force-close-btn"
              >
                <AlertOctagon className="w-3.5 h-3.5" /> Force Close
              </button>
            )}
          </div>
        </section>

        {/* AI summary */}
        <section className="p-4">
          <div className="h-widget mb-2 flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5 sev-low" />
            Generated Summary
          </div>
          <div className="panel-elevated p-3 text-xs leading-relaxed text-foreground" data-testid="incident-ai-summary">
            {incident.ai_summary}
          </div>
          <details className="mt-2">
            <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Raw Event Description
            </summary>
            <div className="mt-1 text-[11px] text-muted-foreground">{incident.description || "—"}</div>
          </details>
        </section>

        {/* Assignment */}
        <section className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="h-widget mb-1.5">Responder Team</div>
            <Select value={team} onValueChange={(v) => { setTeam(v); patch({ team_type: v }); }}>
              <SelectTrigger className="h-9" data-testid="incident-team-select" style={{ borderRadius: 2 }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TEAMS.map((t) => {
                  const meta = TEAM_META[t];
                  const Icon = meta.icon;
                  return (
                    <SelectItem key={t} value={t}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                        {meta.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="h-widget mb-1.5">Assignee</div>
            <Select value={assignee || "unassigned"} onValueChange={(v) => { const nv = v === "unassigned" ? null : v; setAssignee(nv || ""); patch({ assignee: nv }); }}>
              <SelectTrigger className="h-9" data-testid="incident-assignee-select" style={{ borderRadius: 2 }}>
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {ASSIGNEES.map((a) => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </section>

        {/* Notes */}
        <section className="p-4">
          <div className="h-widget mb-1.5">Operator Notes</div>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => notes !== (incident.notes || "") && patch({ notes })}
            placeholder="Write what happened, who responded, outcome…"
            rows={3}
            className="font-mono text-xs"
            style={{ borderRadius: 2 }}
            data-testid="incident-notes"
          />
          <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            saves on blur
          </div>
        </section>

        {/* History */}
        <section className="p-4">
          <div className="h-widget mb-2">Audit Trail</div>
          <ol className="space-y-1.5">
            {(incident.history ?? []).map((h, i) => {
              const m = STATUS_META[h.status];
              return (
                <li key={i} className="flex items-start gap-2 text-[11px] font-mono">
                  <span className="w-1.5 h-1.5 mt-1.5 shrink-0" style={{ background: m?.color || "hsl(var(--primary))" }} />
                  <span className="text-muted-foreground shrink-0">{new Date(h.ts).toLocaleString()}</span>
                  <span className="text-foreground">→ {m?.label || h.status}</span>
                  <span className="text-muted-foreground ml-auto">{h.by || "—"}</span>
                </li>
              );
            })}
          </ol>
        </section>
      </div>
    </div>
  );
};

export default IncidentDetail;
